/**
 * AdminContentReportsTab — DMCA-style queue.
 *
 * Pulls `/api/admin/content-reports?status=open|reviewing|resolved|all`
 * and renders each report side-by-side with the scribe's originality
 * acknowledgement. Admins can resolve with one of three actions:
 *   • keep            — dismiss the complaint.
 *   • warn_writer     — mark resolved with a note; nothing else changes.
 *   • remove_content  — flip `hidden_by_admin: True` on the content row.
 *
 * Co-admins (read-only) see the queue but the resolve button is disabled.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flag, ShieldCheck, ExternalLink, Loader2, CheckCircle2, AlertTriangle, Eye, Ban, MessageSquareWarning } from 'lucide-react';
import toast from 'react-hot-toast';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const REASON_LABEL = {
  copyright: 'Copyright',
  plagiarism: 'Plagiarism',
  ai_unedited: 'AI · unedited',
  harmful: 'Harmful',
  other: 'Other',
};

const ACTION_LABEL = {
  keep: 'Kept (no action)',
  warn_writer: 'Writer warned',
  remove_content: 'Content hidden',
};

const STATUS_OPTIONS = [
  { id: 'open', label: 'Open' },
  { id: 'reviewing', label: 'Reviewing' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'all', label: 'All' },
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(iso); }
};

const ReportCard = ({ r, role, onResolve }) => {
  const [resolution, setResolution] = useState('');
  const [action, setAction] = useState('keep');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const canResolve = role === 'admin' && r.status !== 'resolved';

  const submit = async () => {
    if (!resolution.trim()) {
      toast.error('Add a one-line resolution note.');
      return;
    }
    setBusy(true);
    try {
      await onResolve(r.report_id, resolution.trim(), action);
    } finally { setBusy(false); }
  };

  const ack = r.content?.original_ack;
  const repeatOffender = (r.owner_other_open_reports || 0) >= 2;

  return (
    <article
      className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 sm:p-5 grid grid-cols-1 md:grid-cols-2 gap-4"
      data-testid={`content-report-${r.report_id}`}
    >
      {/* LEFT — the report */}
      <div className="space-y-3">
        <header className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-widest text-rose-300 bg-rose-500/10 border border-rose-400/30 rounded-full px-2 py-0.5">
            {REASON_LABEL[r.reason] || r.reason}
          </span>
          {r.status === 'open' && <span className="text-[10px] font-semibold text-amber-300">● open</span>}
          {r.status === 'reviewing' && <span className="text-[10px] font-semibold text-cyan-300">● reviewing</span>}
          {r.status === 'resolved' && <span className="text-[10px] font-semibold text-emerald-300">● resolved</span>}
          {repeatOffender && (
            <span className="text-[10px] font-bold text-orange-300 inline-flex items-center gap-0.5">
              <AlertTriangle className="w-3 h-3" /> {r.owner_other_open_reports} more open against this writer
            </span>
          )}
        </header>

        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Title</p>
          <button
            type="button"
            onClick={() => r.ref_id && navigate(`/series/${r.ref_id}`)}
            className="text-sm text-indigo-200 hover:text-indigo-100 font-bold inline-flex items-center gap-1.5 underline-offset-2 hover:underline text-left"
            data-testid={`report-${r.report_id}-open-content`}
          >
            {r.content?.cover_image && (
              <img src={r.content.cover_image} alt="" className="w-7 h-7 rounded object-cover border border-slate-700" />
            )}
            "{r.ref_title || 'Untitled'}"
            <ExternalLink className="w-3 h-3" />
          </button>
          {r.content?.hidden_by_admin && (
            <span className="ml-2 text-[10px] font-bold text-rose-300 bg-rose-500/10 border border-rose-400/30 rounded-full px-2 py-0.5">
              HIDDEN
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 text-[12px]">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-0.5">Reporter</p>
            <p className="text-slate-200">{r.reporter_name || r.reporter_user_id}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-0.5">When</p>
            <p className="text-slate-200">{fmtDate(r.created_at)}</p>
          </div>
        </div>

        {r.details && (
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Details</p>
            <p className="text-sm text-slate-200 whitespace-pre-wrap break-words bg-slate-950/60 border border-slate-800 rounded-lg p-2.5">{r.details}</p>
          </div>
        )}

        {r.status === 'resolved' && (
          <div className="rounded-lg bg-emerald-500/5 border border-emerald-400/30 p-2.5">
            <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold mb-1">
              <CheckCircle2 className="w-3 h-3 inline -translate-y-0.5 mr-1" />
              Resolved — {ACTION_LABEL[r.action_taken] || r.action_taken}
            </p>
            <p className="text-sm text-slate-200">{r.resolution || '—'}</p>
            <p className="text-[10px] text-slate-500 mt-1">{fmtDate(r.resolved_at)}</p>
          </div>
        )}
      </div>

      {/* RIGHT — the originality acknowledgement */}
      <div className="rounded-xl bg-slate-950/60 border border-slate-800 p-4 space-y-3">
        <h3 className="text-[10px] uppercase tracking-widest text-amber-300 font-bold inline-flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" /> Scribe's originality pledge
        </h3>
        {ack ? (
          <>
            <p className="text-sm text-slate-200">
              Agreed by{' '}
              <span className="text-indigo-200 font-bold">
                {r.owner?.pen_name || r.owner?.name || r.owner?.email || ack.user_id}
              </span>
            </p>
            <dl className="text-[12px] text-slate-300 space-y-0.5">
              <div className="flex justify-between gap-2"><dt className="text-slate-500">When</dt><dd className="text-slate-200">{fmtDate(ack.agreed_at)}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-slate-500">Version</dt><dd className="text-slate-200">{ack.version || 'v1'}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-slate-500">Hash</dt><dd className="text-slate-400 truncate" title={ack.statement_hash}>{ack.statement_hash}</dd></div>
            </dl>
          </>
        ) : (
          <p className="text-sm text-rose-300 inline-flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" />
            No originality acknowledgement on file — this upload predates the policy.
          </p>
        )}

        {canResolve && (
          <div className="pt-3 border-t border-slate-800 space-y-2">
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Resolve</p>
            <div className="flex gap-1.5 flex-wrap">
              {[
                { id: 'keep', label: 'Keep', icon: Eye, color: 'border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/10' },
                { id: 'warn_writer', label: 'Warn writer', icon: MessageSquareWarning, color: 'border-amber-400/40 text-amber-200 hover:bg-amber-500/10' },
                { id: 'remove_content', label: 'Remove', icon: Ban, color: 'border-rose-400/40 text-rose-200 hover:bg-rose-500/10' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setAction(opt.id)}
                  className={`px-2.5 py-1 text-[11px] rounded-md border ${action === opt.id ? `${opt.color} bg-slate-800` : 'border-slate-700 text-slate-300 hover:bg-slate-800'}`}
                  data-testid={`report-${r.report_id}-action-${opt.id}`}
                >
                  <opt.icon className="w-3 h-3 inline mr-1 -translate-y-px" />
                  {opt.label}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={resolution}
              onChange={(e) => setResolution(e.target.value.slice(0, 1000))}
              placeholder="One-line resolution note (required)…"
              className="w-full rounded-md bg-slate-900 border border-slate-700 text-white text-xs px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-rose-500/40"
              data-testid={`report-${r.report_id}-resolution-input`}
            />
            <button
              type="button"
              onClick={submit}
              disabled={busy || !resolution.trim()}
              className="w-full px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-500 disabled:bg-slate-700 text-white text-xs font-bold inline-flex items-center justify-center gap-1.5"
              data-testid={`report-${r.report_id}-resolve-submit`}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Flag className="w-3.5 h-3.5" />}
              Mark resolved
            </button>
          </div>
        )}
      </div>
    </article>
  );
};

const AdminContentReportsTab = ({ role = 'admin' }) => {
  const [status, setStatus] = useState('open');
  const [data, setData] = useState({ reports: [], counts: {} });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/content-reports?status=${status}`);
      if (r.ok) setData(await r.json());
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (reportId, resolution, action) => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/content-reports/${reportId}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolution, action_taken: action }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Resolve failed');
      }
      toast.success('Report resolved.');
      load();
    } catch (e) {
      toast.error(e.message || 'Resolve failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-content-reports-tab">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-white inline-flex items-center gap-2">
            <Flag className="w-5 h-5 text-rose-400" />
            Content reports
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            DMCA-style queue. Each report ships with the scribe's originality pledge so you can verify quickly.
          </p>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-slate-400">
          {['open', 'reviewing', 'resolved'].map((s) => (
            <span key={s}>
              <span className="text-slate-500 uppercase tracking-widest mr-1">{s}</span>
              <span className="text-white font-bold tabular-nums">{data.counts?.[s] ?? 0}</span>
            </span>
          ))}
        </div>
      </header>

      <div className="flex gap-1 flex-wrap" data-testid="admin-reports-status-pills">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setStatus(s.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
              status === s.id
                ? 'bg-rose-600 border-rose-500 text-white'
                : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-500'
            }`}
            data-testid={`admin-reports-status-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-slate-400 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading queue…
        </p>
      ) : data.reports.length === 0 ? (
        <p className="text-sm text-slate-500 italic py-8 text-center" data-testid="admin-reports-empty">
          Nothing here. {status === 'resolved' ? 'Resolved reports will appear here.' : 'No active complaints — nice.'}
        </p>
      ) : (
        <div className="space-y-3">
          {data.reports.map((r) => (
            <ReportCard key={r.report_id} r={r} role={role} onResolve={resolve} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminContentReportsTab;
